// 물약 퀵슬롯·자동 물약·부활 깃털·비약 끝 알림(D2c). 보유 수와 모든 변경은 장비 장부(gear.ts, 연산 id 멱등)에 있고 여기는 판 안 규칙만 둔다.
// 규칙(PRD 4.6): 퀵슬롯 2칸(HP·MP), 공용 쿨다운 6초, 연속 사용마다 회복량 −20%(마지막 사용 10초 뒤 원래대로, 하한 20%), 개수 제한 없음.
// 회복량 = 최대치(던전 입장 때 고정된 능력치 + 레벨업) × 물약 비율 × 감쇠. 고정 60Hz 스텝에서 돌고, 평소 틱은 비교·감소만 한다(할당 없음).
// 물약을 실제로 마시는 순간에만 장부 연산(id = 판 nonce + 순번) 하나를 만든다(줍기와 같은 이벤트 단위 할당). 저장은 합친 저장(flushIdle)·판 끝.
import { ConsumeKind, Ev } from '../shared/events';
import { AUTO_POTION_OPTIONS, ELIXIRS, POTIONS, POTION_COOLDOWN_TICKS, POTION_DECAY_RESET_TICKS, REVIVE, potionDecay, type PotionDef, type PotionKind } from '../shared/content/consumables';
import { Btn, HeroAct, Scene, combatScene, type ConsumeView, type InputFrame } from '../shared/types';
import { rewardId } from './save';
import type { GameSim } from './game';

/** 종류별 물약(큰 것부터) — 퀵슬롯은 가진 것 중 가장 큰 것을 쓴다 */
const BY_KIND: Readonly<Record<PotionKind, readonly PotionDef[]>> = {
  hp: POTIONS.filter((p) => p.kind === 'hp').sort((a, b) => b.size - a.size),
  mp: POTIONS.filter((p) => p.kind === 'mp').sort((a, b) => b.size - a.size),
};
const DENY = { Cooldown: 0, Empty: 1, Full: 2 } as const;

export class ConsumeState implements ConsumeView {
  potionCd = 0;
  readonly potionCdMax = POTION_COOLDOWN_TICKS;
  decay = 1;
  autoPotion = 0;
  featherUsed = false;
  readonly slotItem: string[] = ['', ''];
  used = 0;
}

export class ConsumableController {
  readonly state = new ConsumeState();
  /** 연속 사용 수(감쇠 단계)와 마지막 사용 틱 */
  private streak = 0;
  private sinceUse = 1e9;
  private runNonce = '';
  private serial = 0;
  /** 마지막으로 본 비약 비트(끝 알림용) */
  private lastMask = 0;
  /** 퀵슬롯 표시를 마지막으로 맞춘 장부 revision */
  private slotRev = -1;

  constructor(private readonly g: GameSim) {}

  /** 판 시작(loadMap): 쿨다운·감쇠·깃털 초기화, 연산 id용 판 nonce */
  begin(): void {
    const s = this.state;
    s.potionCd = 0; s.decay = 1; s.featherUsed = false; s.used = 0;
    this.streak = 0; this.sinceUse = 1e9; this.serial = 0;
    this.runNonce = rewardId();
    this.syncSlots(true);
  }

  setAutoPotion(pct: number): void {
    // 정해진 선택지만(설정 조작 방어)
    this.state.autoPotion = AUTO_POTION_OPTIONS.includes(pct) ? pct : 0;
  }

  /** 퀵슬롯이 쓸 물약(가진 것 중 가장 큰 것). 장부 revision이 바뀔 때만 다시 찾는다(할당 없음) */
  private syncSlots(force = false): void {
    const gear = this.g.gear;
    if (!force && gear.revision === this.slotRev) return;
    this.slotRev = gear.revision;
    const cons = gear.cons;
    for (let k = 0; k < 2; k++) {
      const list = BY_KIND[k === 0 ? 'hp' : 'mp'];
      let id = '';
      for (let i = 0; i < list.length; i++) if ((cons[list[i].id] ?? 0) > 0) { id = list[i].id; break; }
      this.state.slotItem[k] = id;
    }
  }

  /**
   * 고정 스텝(마을·던전, 일시정지 아님). 플레이 시계(비약 시간)는 마을·던전 모두 흐르고, 물약 쿨다운·감쇠·입력·자동 물약은 던전에서만.
   * 비약이 끝나면 Ev.ElixirEnd를 내고 마을이면 능력치를 다시 맞춘다(던전은 입장 때 고정 — 판 끝까지 효과 유지).
   */
  step(f: InputFrame): void {
    const g = this.g, s = this.state;
    g.gear.tickPlay();
    const mask = g.gear.elixirMask();
    if (mask !== this.lastMask) {
      const ended = this.lastMask & ~mask;
      for (let k = 0; k < ELIXIRS.length; k++) if (ended & (1 << k)) g.emit(Ev.ElixirEnd).a = k;
      this.lastMask = mask;
      if (g.flow.scene === Scene.Village) g.refreshBuffs();
    }
    if (!combatScene(g.flow.scene)) return;
    if (s.potionCd > 0) s.potionCd--;
    if (this.sinceUse < 1e9) this.sinceUse++;
    if (this.sinceUse >= POTION_DECAY_RESET_TICKS && this.streak > 0) this.streak = 0;
    s.decay = potionDecay(this.streak);
    this.syncSlots();
    const h = g.hero;
    if (h.hp <= 0) return;
    if (f.pressed & Btn.Potion1) this.usePotion(0, false);
    if (f.pressed & Btn.Potion2) this.usePotion(1, false);
    // 자동 물약: HP가 문턱 아래이고 쿨다운이 끝났으면 HP 칸(물약이 없거나 쿨다운이면 조용히 넘어간다)
    if (s.autoPotion > 0 && s.potionCd === 0 && h.hp < h.maxHp * s.autoPotion) this.usePotion(0, true);
  }

  /** 퀵슬롯 물약 사용. quiet = 자동 물약(거부 알림을 내지 않는다). 성공하면 true */
  usePotion(slot: 0 | 1, quiet: boolean): boolean {
    const g = this.g, s = this.state, h = g.hero;
    if (!combatScene(g.flow.scene) || h.hp <= 0 || h.act === HeroAct.Dead) return false;
    if (s.potionCd > 0) return this.deny(DENY.Cooldown, slot, quiet);
    const id = s.slotItem[slot];
    const def = id ? POTIONS.find((p) => p.id === id) : undefined;
    if (!def) return this.deny(DENY.Empty, slot, quiet);
    const cur = slot === 0 ? h.hp : h.mp, max = slot === 0 ? h.maxHp : h.maxMp;
    if (cur >= max) return this.deny(DENY.Full, slot, quiet);
    // 장부 연산(마시는 순간 한 번): 다른 탭이 이미 써서 없으면 거절 → 마시지 않는다
    const res = g.gear.submit({ id: `po:${this.runNonce}-${this.serial++}`, t: 'use', item: def.id });
    if (res.code !== 'ok') { this.syncSlots(true); return this.deny(DENY.Empty, slot, quiet); }
    const mult = potionDecay(this.streak);
    const amount = Math.max(1, Math.min(max - cur, Math.round(max * def.heal * mult)));
    if (slot === 0) h.hp += amount; else h.mp += amount;
    s.potionCd = POTION_COOLDOWN_TICKS;
    this.streak++; this.sinceUse = 0;
    s.decay = potionDecay(this.streak);
    s.used++;
    this.syncSlots(true);
    const e = g.emit(Ev.Consume);
    e.x = h.x; e.z = h.z; e.a = slot === 0 ? ConsumeKind.HpPotion : ConsumeKind.MpPotion; e.b = amount; e.c = def.size; e.d = mult;
    e.flags = quiet ? 1 : 0;
    return true;
  }

  private deny(reason: number, slot: number, quiet: boolean): boolean {
    if (!quiet) { const e = this.g.emit(Ev.ConsumeDenied); e.a = reason; e.b = slot; e.c = this.state.potionCd; }
    return false;
  }

  /**
   * 쓰러지는 순간(flow.defeat 첫 줄): 부활 깃털이 있고 이번 판에 아직 안 썼으면 하나 써서 HP 50%로 일어난다(잠깐 무적).
   * 장부 연산이 거절되면(다른 탭이 써서 없음) 그대로 쓰러진다
   */
  tryRevive(): boolean {
    const g = this.g, s = this.state, h = g.hero;
    if (!combatScene(g.flow.scene) || s.featherUsed || (g.gear.cons['feather_revive'] ?? 0) < 1) return false;
    const res = g.gear.submit({ id: `fr:${this.runNonce}-${this.serial++}`, t: 'use', item: 'feather_revive' });
    if (res.code !== 'ok') return false;
    s.featherUsed = true;
    h.hp = Math.max(1, Math.round(h.maxHp * REVIVE.hp));
    g.heroController.revive(REVIVE.invulnTicks);
    const e = g.emit(Ev.Consume);
    e.x = h.x; e.z = h.z; e.a = ConsumeKind.Feather; e.b = h.hp;
    return true;
  }
}
